import { useTranslations } from 'next-intl';
import { ReactNode, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { DisplayedLeverageTypeRawValue, TradeModeEvent } from '@flipster/utils/segment/events/trade-mode';

import {
  DisplayedLeverageType,
  useDisplayedLeverage,
} from '@web/app/[locale]/(web)/trade/_misc/etc-section/perpetual/your-postions/position-table/useDisplayedLeverage';

interface LeverageTypeOptions {
  label: ReactNode;
  desc: ReactNode;
  value: DisplayedLeverageType;
}

const OptionLabelMap: Record<DisplayedLeverageType, DisplayedLeverageTypeRawValue> = {
  positionLeverage: 'Position leverage',
  effectiveLeverage: 'Effective leverage',
};

const Options: LeverageTypeOptions[] = [
  {
    label: <FormattedMessage id='Autogen_common.r251126.s_Position_leverage' />,
    desc: <FormattedMessage id='Autogen_common.r251126.s_Position_Leverage_Desc' />,
    value: 'positionLeverage',
  },
  {
    label: <FormattedMessage id='Autogen_common.r251126.s_Effective_leverage' />,
    desc: <FormattedMessage id='Autogen_common.r251126.s_Effective_Leverage_Desc' />,
    value: 'effectiveLeverage',
  },
];

interface Props {
  onBack: () => void;
  onError: (reason: string) => void;
  onSuccess: () => void;
}

const DisplayedLeverage = ({ onBack: onBackProp, onError, onSuccess }: Props) => {
  const t = useTranslations();
  const [leverageType, setLeverageType] = useDisplayedLeverage();

  const [mutableLeverageType, setMutableLeverageType] = useState(leverageType);

  const onSubmit = () => {
    segment.track(TradeModeEvent.TradeSettingsConfirmClick, {
      setting_name: 'Set displayed leverage',
      option_name: OptionLabelMap[mutableLeverageType],
    });

    try {
      setLeverageType(mutableLeverageType);
      snackbar.success({
        message: <FormattedMessage id='Autogen_common.r251126.s_Leverage_Setting_Updated' />,
      });
      onSuccess();
    } catch {
      onError(t('Common.r250401.Unexpected_error_message_multiline'));
    }
  };

  const onChange = (value: DisplayedLeverageType) => {
    setMutableLeverageType(value);
  };

  const onBack = () => {
    segment.track(TradeModeEvent.TradeSettingsBackClick, { setting_name: 'Set displayed leverage' });
    onBackProp();
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Autogen_common.r251126.s_Set_displayed_leverage')} />
      </Modal.Header>
      <Modal.Content>
        <fieldset
          className={vstack({
            p: 16,
            gap: 16,
            rounded: 16,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-3',
          })}
        >
          {Options.map((item) => (
            <div
              onClick={() => onChange(item.value)}
              key={item.value}
              className={vstack({ alignItems: 'flex-start', gap: 4 })}
            >
              <RadioButton
                name='displayed-leverage'
                value={item.value}
                checked={mutableLeverageType === item.value}
                onChange={(e) => onChange(e.currentTarget.value as DisplayedLeverageType)}
                css={{ color: 'content.default.level-5' }}
              >
                {item.label}
              </RadioButton>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', cursor: 'pointer' })}>
                {item.desc}
              </p>
            </div>
          ))}
        </fieldset>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          hierarchy='secondary'
          size='large'
          color='neutral'
          label={t('Autogen_common.r251126.s_Back')}
          onClick={onBack}
        />
        <Modal.Button
          hierarchy='primary'
          size='large'
          color='accent'
          label={t('Autogen_common.r251126.s_Confirm')}
          onClick={onSubmit}
          disabled={leverageType === mutableLeverageType}
        />
      </Modal.Footer>
    </>
  );
};

export default DisplayedLeverage;
